import { writable, get } from 'svelte/store';
import { ApiError } from '../api/client';
import { getPracticeQuota, type PracticeQuota } from '../api/practice';

export type QuotaStoreState = {
  quota: PracticeQuota | null;
  loading: boolean;
};

const INITIAL: QuotaStoreState = { quota: null, loading: false };

function createQuotaStore() {
  const { subscribe, set, update } = writable<QuotaStoreState>({ ...INITIAL });
  let bound: string | null = null;
  let generation = 0;

  async function load(): Promise<void> {
    const mine = ++generation;
    update((s) => ({ ...s, loading: true }));
    try {
      const quota = await getPracticeQuota();
      if (mine === generation) set({ quota, loading: false });
    } catch (e) {
      if (mine === generation) update((s) => ({ ...s, loading: false }));
      if (!(e instanceof ApiError)) throw e;
    }
  }

  function reset(): void {
    generation += 1;
    set({ ...INITIAL });
  }

  return {
    subscribe,

    // Null until auth settles: the endpoint is auth-optional, so an earlier read is the guest's.
    bind(identity: string | null): void {
      if (identity === bound) return;
      bound = identity;
      reset();
      if (identity) void load();
    },

    refresh: load,

    set(quota: PracticeQuota): void {
      generation += 1;
      set({ quota, loading: false });
    },

    decrementLocally(): void {
      update((s) =>
        s.quota ? { ...s, quota: { ...s.quota, remaining: Math.max(0, s.quota.remaining - 1) } } : s,
      );
    },

    current(): PracticeQuota | null {
      return get({ subscribe }).quota;
    },

    reset,
  };
}

export const quotaStore = createQuotaStore();
